import React from "react";
import Box from "@mui/material/Box";
import { useUserAuth } from "../contexts/authContext";
import { ConvertISOToDate } from "../util";
import { DataGrid } from "@mui/x-data-grid";
import { CustomPagination } from "../customPagination";
import { Button } from "@mui/material";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { CustomGridToolbar } from "../customGridToolbar";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

export function DataLoggingDAAQuery() {
    const [data, setData] = React.useState([]);
    const [inputEnd, setInputEnd] = React.useState("");
    const [inputStart, setInputStart] = React.useState("");
    const [altitudeMin, setAltitudeMin] = React.useState(0);
    const [altitudeMax, setAltitudeMax] = React.useState(0);
    const [ownshipValue, setOwnshipValue] = React.useState("");
    const [alertValue, setAlertValue] = React.useState("");
    const [sourceType, setSourceType] = React.useState("");
    const [awaitingResults, setAwaitingResults] = React.useState(false);
    const { handleFailedFetch, setSnackbar } = useUserAuth();

    const handleSubmit = () => {
        if (inputStart == "" || inputEnd == "") {
            setSnackbar({ children: "Please fill out the required fields", severity: "error" });
            return false;
        }

        const query = {
            start_param: new Date(inputStart).toLocaleDateString(),
            end_pararm: new Date(inputEnd).toLocaleDateString(),
            ownship_param: ownshipValue,
            alert_param: alertValue,
            source_type_param: sourceType,
            alt_param_min: altitudeMin,
            alt_param_max: altitudeMax,
            polygon_param: null
        };
        setAwaitingResults(true);

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(query)
        };
        fetch("api/dl/daaquery", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => {
                setData(data);
                setAwaitingResults(false);
            })
            .catch((err) => handleFailedFetch(err));
    };

    const columns = [
        {
            field: "message_time",
            headerName: "Message Time",
            minWidth: 200,
            type: "dateTime",
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "alert_level",
            headerName: "Alert Level",
            flex: 1,
            minWidth: 100
        },
        {
            field: "ownship_location",
            headerName: "Ownship Location",
            minWidth: 125,
            flex: 1,
            valueFormatter: (value) => `${value.x.toFixed(2)}, ${value.y.toFixed(2)}`
        },
        {
            field: "ownship_alt_hae_m",
            headerName: "Ownship Alt",
            minWidth: 50,
            flex: 1
        },
        {
            field: "ownship_uuid",
            headerName: "Ownship UUID",
            minWidth: 300,
            flex: 1
        },
        {
            field: "intruder_location",
            headerName: "Intruder Location",
            minWidth: 125,
            flex: 1,
            valueFormatter: (value) => `${value.x.toFixed(2)}, ${value.y.toFixed(2)}`
        },
        {
            field: "intruder_alt_hae_m",
            headerName: "Intruder Alt",
            minWidth: 50,
            flex: 1
        },
        {
            field: "intruder_uuid",
            headerName: "Intruder UUID",
            minWidth: 300,
            flex: 1
        },
        {
            field: "intruder_source_type",
            headerName: "Intruder Source Type",
            minWidth: 150,
            flex: 1
        },
        {
            field: "date_recorded",
            headerName: "Date Recorded",
            minWidth: 200,
            type: "dateTime",
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        }
    ];

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: "16px" }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
                        gap: "10px"
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Start Time *"
                            value={dayjs(inputStart)}
                            onChange={(value) => setInputStart(value)}
                            slotProps={{ textField: { inputProps: { "data-testid": "start-date-picker" } } }}
                        />
                        <DatePicker
                            label="Stop Time *"
                            value={dayjs(inputEnd)}
                            onChange={(value) => setInputEnd(value)}
                            slotProps={{ textField: { inputProps: { "data-testid": "stop-date-picker" } } }}
                        />
                    </LocalizationProvider>
                    <TextField
                        onChange={(e) => setOwnshipValue(e.target.value)}
                        inputProps={{ "data-testid": "ownship" }}
                        label="Ownship UUID "
                        value={ownshipValue}
                        variant="outlined"
                        fullWidth
                    />
                    <TextField
                        onChange={(e) => setAlertValue(e.target.value)}
                        inputProps={{ "data-testid": "alertlevel" }}
                        label="Alert Level "
                        value={alertValue}
                        variant="outlined"
                        fullWidth
                    />
                </Box>
                <Box
                    sx={{
                        gridColumn: { md: "1/3" },
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
                        gap: "10px"
                    }}
                >
                    <TextField
                        onChange={(e) => setSourceType(e.target.value)}
                        inputProps={{ "data-testid": "sourceType" }}
                        label="Source Type "
                        value={sourceType}
                        variant="outlined"
                        fullWidth
                    />

                    <TextField
                        InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        onChange={(e) => setAltitudeMin(e.target.value)}
                        inputProps={{ "data-testid": "altitudeMin" }}
                        label="Minimum Altitude "
                        value={altitudeMin}
                        variant="outlined"
                        type="number"
                        fullWidth
                    />
                    <TextField
                        InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        onChange={(e) => setAltitudeMax(e.target.value)}
                        inputProps={{ "data-testid": "altitudeMax" }}
                        label="Maximum Altitude "
                        value={altitudeMax}
                        variant="outlined"
                        type="number"
                        fullWidth
                    />
                    <Button
                        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                        data-testid="RunQuery"
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={awaitingResults}
                        color="primary"
                    >
                        {awaitingResults ? <>Awaiting Results</> : <>Run Query</>}
                    </Button>
                </Box>
            </Box>
            <Box sx={{ height: 600 }}>
                <DataGrid
                    disableVirtualization
                    rows={data}
                    columns={columns}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    pagination
                    slots={{ pagination: CustomPagination, Toolbar: CustomGridToolbar }}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                />
            </Box>
        </>
    );
}
